// src/services/domain.service.js
import { api } from './api';

class DomainService {
    /**
     * Get current user's domain information
     * SECURITY: Fetches company data from backend using authenticated user's context
     * Backend verifies JWT token and returns only the company that the authenticated user belongs to
     */
    async getDomainInfo() {
        try {
            //console.log('🔍 Fetching company info from backend (authenticated endpoint)...');

            // SECURITY: Backend uses JWT token to get user's company_id and returns only their company
            // No company ID needed in request - backend extracts it from authenticated user
            const response = await api.get('/api/companies/me');

            if (response.data.success && response.data.data) {
                //console.log('✅ Company data fetched successfully:', response.data.data.name);
                //console.log('📅 Company created_at:', response.data.data.created_at);
                return response.data.data;
            }

            console.warn('⚠️ Backend returned no data, using fallback');
            return this.getLocalDomainInfo();
        } catch (error) {
            console.error('❌ Error fetching company info from backend:', error.response?.data || error.message);
            console.warn('⚠️ Falling back to localStorage for local development');
            // Fallback to localStorage for local development
            return this.getLocalDomainInfo();
        }
    }

    /**
     * Get domain registration date
     * Returns Date object or null
     * Note: Uses created_at from companies table as registration date
     */
    async getDomainRegistrationDate() {
        try {
            const domainInfo = await this.getDomainInfo();

            // Priority: created_at is the actual column in companies table
            if (domainInfo && domainInfo.created_at) {
                return new Date(domainInfo.created_at);
            }

            // Fallback: registration_date if added in future
            if (domainInfo && domainInfo.registration_date) {
                return new Date(domainInfo.registration_date);
            }

            return null;
        } catch (error) {
            console.error('Error fetching domain registration date:', error);
            return null;
        }
    }

    /**
     * Fallback: Get domain info from localStorage
     * Used for local development and backward compatibility
     */
    getLocalDomainInfo() {
        try {
            // Try to get from company settings
            const company = localStorage.getItem('company');
            if (company) {
                const companyData = JSON.parse(company);

                // Return domain info from company data
                return {
                    domain: companyData.domain || 'company',
                    name: companyData.name || 'Your Company',
                    registration_date: companyData.registration_date || companyData.created_at || '2024-04-01',
                    created_at: companyData.created_at || '2024-04-01'
                };
            }

            // Default fallback
            return {
                domain: 'company',
                name: 'Your Company',
                registration_date: '2024-04-01', // Default start date
                created_at: '2024-04-01'
            };
        } catch (error) {
            console.error('Error loading domain info from localStorage:', error);
            return {
                domain: 'company',
                name: 'Your Company',
                registration_date: '2024-04-01',
                created_at: '2024-04-01'
            };
        }
    }

    /**
     * Update domain information
     * SECURITY: Backend should validate user has permission to update domain
     */
    async updateDomainInfo(domainData) {
        try {
            const response = await api.put('/api/domain/info', domainData);
            return response.data;
        } catch (error) {
            console.error('Error updating domain info:', error);
            throw error;
        }
    }

    /**
     * Get domain settings
     */
    async getDomainSettings() {
        try {
            const response = await api.get('/api/domain/settings');
            return response.data.data || {};
        } catch (error) {
            console.error('Error fetching domain settings:', error);
            return {};
        }
    }
}

export default new DomainService();
